import { CalendarClock, ChevronLeft, ChevronRight, ExternalLink, Flag, RefreshCw, ZoomOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  type QuickRange,
  type TimeRange,
  absolute,
  describe,
  fromLocalInput,
  MIN_SPAN_S,
  REFRESH_OPTIONS,
  relative,
  resolve,
  spanOf,
  toLocalInput,
} from '../lib/timeRange';

interface Props {
  quickRanges: QuickRange[];
  range: TimeRange;
  nowSec: number;
  maxSpanSeconds: number;
  onRange: (range: TimeRange) => void;
  onShift: (direction: -1 | 1) => void;
  onZoomOut: () => void;
  refreshKey: string;
  onRefreshKey: (key: string) => void;
  onRefreshNow: () => void;
  refreshing: boolean;
  updatedAt: number | null;
  showIncidents: boolean;
  onToggleIncidents: () => void;
  grafanaUrl: string | null;
}

/**
 * One row, above every chart, scoping all of them: the presets first because
 * they are what everyone reaches for, the custom window behind a button, and
 * refresh on the right. The caption in the middle is the one thing that must
 * always be true -- it says which window the whole screen is drawing.
 */
export function TimeToolbar({
  quickRanges,
  range,
  nowSec,
  maxSpanSeconds,
  onRange,
  onShift,
  onZoomOut,
  refreshKey,
  onRefreshKey,
  onRefreshNow,
  refreshing,
  updatedAt,
  showIncidents,
  onToggleIncidents,
  grafanaUrl,
}: Props) {
  const [customOpen, setCustomOpen] = useState(false);
  const window = resolve(range, nowSec);
  const [fromInput, setFromInput] = useState(toLocalInput(window.from));
  const [toInput, setToInput] = useState(toLocalInput(window.to));
  const [customError, setCustomError] = useState<string | null>(null);

  // The popover opens on the window currently drawn, whatever set it.
  useEffect(() => {
    if (!customOpen) return;
    setFromInput(toLocalInput(window.from));
    setToInput(toLocalInput(window.to));
    setCustomError(null);
  }, [customOpen, window.from, window.to]);

  // Escape closes it, like every other transient surface; a keyboard user is
  // otherwise left tabbing to Cancel.
  useEffect(() => {
    if (!customOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCustomOpen(false);
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [customOpen]);

  const applyCustom = () => {
    const from = fromLocalInput(fromInput);
    const to = fromLocalInput(toInput);
    if (from === null || to === null) return setCustomError('Both ends are needed.');
    if (to - from < MIN_SPAN_S) return setCustomError('The window must be at least a minute, and end after it starts.');
    if (to - from > maxSpanSeconds) return setCustomError(`The window can be at most ${maxSpanSeconds / 86_400} days.`);
    setCustomOpen(false);
    onRange(absolute(from, to));
  };

  const atNow = range.kind === 'relative' || range.to >= nowSec - 5;
  const canZoomOut = spanOf(range) < maxSpanSeconds;

  return (
    <div className="wt-toolbar" role="toolbar" aria-label="Time range">
      <button
        type="button"
        className="btn btn-secondary btn-icon"
        title="Earlier: move the window back by half"
        aria-label="Earlier"
        onClick={() => onShift(-1)}
      >
        <ChevronLeft size={15} />
      </button>
      <div className="wt-range" role="group" aria-label="Quick ranges">
        {quickRanges.map((option) => (
          <button
            key={option.key}
            type="button"
            className={range.kind === 'relative' && range.key === option.key ? 'active' : ''}
            aria-pressed={range.kind === 'relative' && range.key === option.key}
            onClick={() => onRange(relative(option))}
          >
            {option.key}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-secondary btn-icon"
        title={atNow ? 'Already at now' : 'Later: move the window forward by half'}
        aria-label="Later"
        disabled={atNow}
        onClick={() => onShift(1)}
      >
        <ChevronRight size={15} />
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-icon"
        title={canZoomOut ? 'Zoom out: twice the window' : 'At the widest window'}
        aria-label="Zoom out"
        disabled={!canZoomOut}
        onClick={onZoomOut}
      >
        <ZoomOut size={15} />
      </button>

      <div className="wt-pop-host">
        <button
          type="button"
          className={`btn btn-secondary${range.kind === 'absolute' ? ' is-active' : ''}`}
          aria-expanded={customOpen}
          onClick={() => setCustomOpen((open) => !open)}
        >
          <CalendarClock size={14} /> Custom
        </button>
        {customOpen && (
          <>
            <div className="wt-pop-backdrop" onClick={() => setCustomOpen(false)} />
            <form
              className="wt-pop"
              onSubmit={(event) => {
                event.preventDefault();
                applyCustom();
              }}
            >
              <label>
                From
                <input
                  className="input"
                  type="datetime-local"
                  value={fromInput}
                  onChange={(event) => setFromInput(event.target.value)}
                />
              </label>
              <label>
                To
                <input
                  className="input"
                  type="datetime-local"
                  value={toInput}
                  onChange={(event) => setToInput(event.target.value)}
                />
              </label>
              {customError && <span className="wt-pop-error">{customError}</span>}
              <div className="wt-pop-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setCustomOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Apply
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <span className={`wt-toolbar-caption${range.kind === 'relative' ? ' is-relative' : ''}`} aria-live="polite">
        {describe(range)}
        {range.kind === 'absolute' && (
          <>
            {' · '}
            <button type="button" className="wt-linkish" onClick={() => onRange(relative(quickRanges[3] ?? quickRanges[0]))}>
              back to now
            </button>
          </>
        )}
      </span>

      <span className="wt-spacer" />

      <button
        type="button"
        className={`btn btn-secondary${showIncidents ? ' is-active' : ''}`}
        aria-pressed={showIncidents}
        title="Draw the incidents sphere-watch had open during the window as bands"
        onClick={onToggleIncidents}
      >
        <Flag size={14} /> Incidents
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        title={updatedAt ? `Updated ${new Date(updatedAt).toLocaleTimeString()}` : 'Refresh'}
        onClick={onRefreshNow}
        disabled={refreshing}
      >
        <RefreshCw size={14} className={refreshing ? 'wt-spin' : ''} /> <span className="wt-toolbar-label">Refresh</span>
      </button>
      <label className="wt-select-label">
        <span className="sr-only">Auto-refresh</span>
        <select
          className="wt-select"
          value={refreshKey}
          onChange={(event) => onRefreshKey(event.target.value)}
          title={
            range.kind === 'relative'
              ? 'Auto-refresh'
              : 'Auto-refresh re-reads a fixed window; pick a quick range to follow now'
          }
        >
          {REFRESH_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {grafanaUrl && (
        <a className="btn btn-secondary" href={grafanaUrl} target="_blank" rel="noreferrer">
          Grafana <ExternalLink size={13} />
        </a>
      )}
    </div>
  );
}
